import { type ButtonHTMLAttributes, type ReactNode, forwardRef } from "react";
import { cn } from "@/lib/cn";

export interface CounterButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  value: number;
  /** This counter's filter is the one applied now. */
  selected: boolean;
  /** "danger" paints a non-zero value red (problems, dead proxies). */
  tone?: "default" | "danger";
  /** Optional status dot before the label, e.g. "bg-success". */
  dot?: string;
  trailing?: ReactNode;
}

/**
 * CounterButton — a "Label 12" figure in a page subtitle that doubles as a
 * filter toggle. forwardRef + rest props so it can be a Radix trigger (asChild).
 */
export const CounterButton = forwardRef<HTMLButtonElement, CounterButtonProps>(
  ({ label, value, selected, tone = "default", dot, trailing, className, ...rest }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      {...rest}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        selected ? "bg-accent/15 text-fg-primary" : "text-fg-muted hover:bg-bg-secondary",
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={cn("h-1.5 w-1.5 rounded-full", dot)} />}
      <span>{label}</span>
      <span
        className={cn(
          "font-semibold tabular-nums",
          tone === "danger" && value > 0 ? "text-danger" : "text-fg-primary",
        )}
      >
        {value}
      </span>
      {trailing}
    </button>
  ),
);
CounterButton.displayName = "CounterButton";
